import type { DataTableFilterMeta } from 'primereact/datatable';
import { DEFAULT_ENTITY_NODES_LIMIT } from '@/constants/graph.constants';
import {
  DEFAULT_REMAP_SEARCH_LIMIT,
  PIPELINE_SUPPORTED_FILTER_MODES,
  REMAP_PIPELINE_NAMES,
  REMAP_SEARCH_BINDINGS,
  SEARCH_RELEVANCE_COLUMNS,
} from '@/constants/pipelines.constants';
import type {
  Pipeline,
  PipelineStep,
  PredicateSpec,
} from '@/interfaces/pipelines.interface';
import type { FilterConstraint } from '@/helpers/graph.helpers';

/** Flatten DataTableFilterMeta to constraints (mirrors useTableGraph.flattenFilterMeta). */
export function flattenFilterMetaToConstraints(
  filters: DataTableFilterMeta | undefined,
): FilterConstraint[] {
  if (!filters || typeof filters !== 'object') return [];
  const constraints: FilterConstraint[] = [];
  for (const field of Object.keys(filters)) {
    const meta = (
      filters as Record<
        string,
        FilterConstraint | { constraints?: FilterConstraint[] }
      >
    )[field];
    if (!meta) continue;
    if (
      Array.isArray((meta as { constraints?: FilterConstraint[] }).constraints)
    ) {
      for (const c of (meta as { constraints: FilterConstraint[] })
        .constraints) {
        constraints.push({
          field,
          value: c?.value != null ? String(c.value) : '',
          matchMode: c?.matchMode || 'contains',
        });
      }
    } else {
      const single = meta as FilterConstraint;
      constraints.push({
        field,
        value: single?.value != null ? String(single.value) : '',
        matchMode: single?.matchMode || 'contains',
      });
    }
  }
  return constraints;
}

const sanitizeFilterField = (field: string): string | null => {
  const sanitized = field.replace(/[^a-zA-Z0-9_]/g, '');
  return sanitized.length > 0 ? sanitized : null;
};

/**
 * Validates a node label before it is sent as a pipeline `label` arg. Labels
 * flow as structured JSON (not interpolated into Cypher), so the backend owns
 * charset enforcement; here we just reject empty/blank labels, which would
 * otherwise produce a silently-broken pipeline.
 */
const requireLabel = (label: string, context: string): string => {
  const trimmed = label?.trim();
  if (!trimmed) {
    throw new Error(`${context}: a non-empty node label is required`);
  }
  return trimmed;
};

const MATCH_MODE_TO_PREDICATE_OP: Record<string, string> = {
  contains: 'contains',
  notcontains: 'notContains',
  equals: '=',
  notequals: '!=',
  lt: '<',
  lte: '<=',
  gt: '>',
  gte: '>=',
  startswith: 'startsWith',
  endswith: 'endsWith',
};

/** True when every active filter uses a pipeline-supported match mode. */
export function areFiltersPipelineCompatible(
  filters?: DataTableFilterMeta,
): boolean {
  const constraints = flattenFilterMetaToConstraints(filters);
  return constraints.every((c) => {
    if (!c.value?.trim()) return true;
    const mode = (c.matchMode || 'contains').toLowerCase();
    return PIPELINE_SUPPORTED_FILTER_MODES.has(mode);
  });
}

export function filterConstraintsToPredicates(
  constraints: FilterConstraint[],
): PredicateSpec[] {
  const predicates: PredicateSpec[] = [];
  for (const c of constraints) {
    const value = c.value;
    if (value === undefined || value === null || String(value).trim() === '') {
      continue;
    }
    const field = sanitizeFilterField(c.field);
    if (!field) continue;
    const mode = (c.matchMode || 'contains').toLowerCase();
    if (!PIPELINE_SUPPORTED_FILTER_MODES.has(mode)) continue;
    const op = MATCH_MODE_TO_PREDICATE_OP[mode] ?? 'contains';
    predicates.push({ field, op, value: String(value).trim() });
  }
  return predicates;
}

export function buildOrderByClause(
  sortField: string | null | undefined,
  sortOrder: 1 | -1,
  sortNumeric: boolean,
  sortDateTime: boolean,
): string {
  const safeField = sanitizeFilterField(sortField ?? '') ?? 'id';
  const dir = sortOrder === 1 ? 'asc' : 'desc';
  if (sortDateTime) {
    return `${safeField}:${dir}:datetime`;
  }
  if (sortNumeric) {
    return `${safeField}:${dir}:numeric`;
  }
  return `${safeField}:${dir}`;
}

export interface EntityListPipelineOptions {
  entityType: string;
  limit?: number;
  offset?: number;
  sortField?: string | null;
  sortOrder?: 1 | -1;
  sortNumeric?: boolean;
  sortDateTime?: boolean;
  filters?: DataTableFilterMeta;
  fields?: string[];
  /**
   * Ask the `list_nodes` sink for `total_count`: the EXACT row total before
   * offset/limit, and filter-aware.
   *
   * Off by default and requested per caller, for two reasons. It costs an
   * extra `RETURN count(1)` and, more importantly, routes the sink off the
   * OpenSearch offload onto the graph backend for that request — so only a
   * surface that actually shows the number should pay for it. And
   * `buildEntityListCardConfig` in `graphViewState.helpers` shares this builder
   * for persisted dashboard cards that show every row and read no total, where
   * the flag would be baked into their saved payload forever.
   */
  includeTotal?: boolean;
}

export function buildEntityListPipeline(
  options: EntityListPipelineOptions,
): Pipeline {
  const {
    entityType,
    limit = DEFAULT_ENTITY_NODES_LIMIT,
    offset = 0,
    sortField,
    sortOrder = 1,
    sortNumeric = false,
    sortDateTime = false,
    filters,
    fields,
    includeTotal = false,
  } = options;

  const predicates = filterConstraintsToPredicates(
    flattenFilterMetaToConstraints(filters),
  );

  const selectArgs: Record<string, unknown> = {
    label: requireLabel(entityType, 'buildEntityListPipeline'),
  };
  if (predicates.length > 0) {
    selectArgs.predicates = predicates;
  }

  const orderBy = buildOrderByClause(
    sortField,
    sortOrder,
    sortNumeric,
    sortDateTime,
  );

  const listArgs: Record<string, unknown> = {
    limit: Math.floor(limit),
    offset: Math.floor(offset),
    order_by: orderBy,
    // Exact row count before pagination, so a caller can say "20 of 39" instead
    // of guessing from the page it just received. `list_nodes` forwards this to
    // its `to_table` (see review_mcp `regmacros_tables._build_list_nodes`).
    //
    // First page only. It costs an extra `RETURN count(1)` and, more importantly,
    // routes the sink off the OpenSearch offload onto the graph backend
    // (`routing_rules_rowset.route_to_table`) — so asking on every page would tax
    // all paging on OS-backed projects. The total cannot change while paging
    // within one result, and every input that *would* change it (filters, sort)
    // resets the grid to the first page, so page one is exactly when it is both
    // needed and correct.
    //
    // Opt-in per caller, not inferred from `offset === 0`: a published dashboard
    // card is built with no offset at all, so an offset-only rule stamped
    // `include_total` onto every entity-list card. Those cards never page and
    // never read the count — they would have paid the de-offloaded route and an
    // extra count pass on every dashboard render, for a number nothing shows.
    ...(includeTotal && Math.floor(offset) === 0
      ? { include_total: true }
      : {}),
  };
  if (fields && fields.length > 0) {
    listArgs.fields = fields;
  }

  return {
    name: REMAP_PIPELINE_NAMES.ENTITY_LIST,
    steps: [
      {
        op: 'select_label',
        args: selectArgs,
        as: 'nodes',
      },
      {
        op: 'list_nodes',
        from: 'nodes',
        args: listArgs,
        as: 'rows',
      },
    ],
    emit: 'last',
  };
}

export function buildAdjacencyCountsPipeline(
  nodeLabel: string,
  nodeId: string,
): Pipeline {
  return {
    name: REMAP_PIPELINE_NAMES.ADJACENCY_COUNTS,
    steps: [
      {
        op: 'select_label',
        args: {
          label: requireLabel(nodeLabel, 'buildAdjacencyCountsPipeline'),
          predicates: [{ field: 'id', op: '=', value: nodeId }],
        },
        as: 'source',
      },
      {
        op: 'adjacency_counts',
        from: 'source',
        args: { direction: 'both' },
        as: 'counts',
      },
    ],
    emit: 'last',
  };
}

export interface ExpandToLabelPipelineOptions {
  sourceLabel: string;
  nodeId: string;
  targetLabel: string;
  relTypes?: string[];
  maxNodes?: number;
  maxRelationships?: number;
}

export function buildExpandToLabelPipeline(
  options: ExpandToLabelPipelineOptions,
): Pipeline {
  const {
    sourceLabel,
    nodeId,
    targetLabel,
    relTypes,
    maxNodes = 1000,
    maxRelationships = 5000,
  } = options;

  const expandArgs: Record<string, unknown> = {
    target_label: requireLabel(targetLabel, 'buildExpandToLabelPipeline'),
    direction: 'both',
  };
  if (relTypes && relTypes.length > 0) {
    expandArgs.rels = relTypes;
  }

  return {
    name: REMAP_PIPELINE_NAMES.EXPAND_TO_LABEL,
    steps: [
      {
        op: 'select_label',
        args: {
          label: requireLabel(sourceLabel, 'buildExpandToLabelPipeline'),
          predicates: [{ field: 'id', op: '=', value: nodeId }],
        },
        as: 'source',
      },
      {
        op: 'expand_to_label',
        from: 'source',
        args: expandArgs,
        as: 'expanded',
      },
      {
        op: 'project_graph',
        from: 'expanded',
        args: {
          max_nodes: maxNodes,
          max_relationships: maxRelationships,
          // Same as exploration replay: open (default) neighbourhood re-adds
          // every 1-hop neighbour of the matched target (e.g. a Person's
          // manager via person_manages_person). Induced keeps only the source
          // + expand_to_label targets and the edges between them.
          induced: true,
        },
        as: 'graph',
      },
    ],
    emit: 'last',
  };
}

/** True when entity fetch can use pipelines (compatible filters). */
export function canUsePipelineForEntityFetch(options: {
  filters?: DataTableFilterMeta;
}): boolean {
  return areFiltersPipelineCompatible(options.filters);
}

export interface RemapSearchPipelineOptions {
  /** Free text: a name, identifier, code or literal value in node properties. */
  query: string;
  /** Also match the query as a substring of exact keyword fields. */
  contains?: boolean;
  /** How many seed nodes to materialise. */
  limit?: number;
  maxNodes?: number;
  maxRelationships?: number;
  /**
   * Narrow the search to these record types.
   *
   * Worth far more than a filter: a scope that resolves to ONE label makes the
   * match set single-label, and only then does the mixed-seed degradation
   * below stop applying — so scoping is what buys per-hit relevance.
   */
  labels?: string[];
  /**
   * Property conditions, in the search tool's own `{field, op, value}` grammar.
   *
   * Computed forms (`transform`, `{left, op, right}`, runtime `param`) are
   * rejected loudly by the backend rather than ignored, so a caller must not
   * pass them.
   */
  filters?: Array<Record<string, unknown>>;
}

/**
 * Compiles a reMap text search into one pipeline.
 *
 * The `search` op is the pipeline's SOURCE (no `from`), unscoped: reMap
 * searches every label and lets the matches decide which ones came back. The
 * graph the canvas renders is its induced projection — same `induced: true`
 * reasoning as `compileExplorationPipeline`: the matches are the population
 * the user asked for, so draw those nodes and only the edges between them
 * rather than every neighbour of every hit.
 *
 * The pipeline ALSO emits a `rows` Table of `id` + the four relevance columns.
 * That Table is the only way relevance can reach the UI at all: it rides the
 * search handle's hit records, and the emitted NodeSet / Graph carriers cannot
 * express it. Two constraints shape how it is built:
 *
 * - It reads DIRECTLY off the search binding. Any step in between produces a
 *   NodeSet that no longer carries the hit records, and the backend rejects
 *   the relevance fields outright.
 * - It uses the `rows_from_nodes` / `to_table` PRIMITIVES with
 *   `allow_mixed: true`, not the `list_nodes` macro. An unscoped search that
 *   matches several labels yields a MIXED-label NodeSet, which `list_nodes`
 *   REJECTS (`_load_nodeset` refuses a mixed set by default) — it would fail
 *   the whole pipeline. The mixed seed instead projects the addressable `id`
 *   alone, so a multi-label search degrades to "no relevance" while a
 *   single-label one still gets all four columns.
 *
 * No `order_by`: it cannot name `search_score` (absent from a mixed set's
 * columns) and it does not need to — this Table is only an id-to-relevance
 * lookup, never displayed in row order.
 */
export function buildRemapSearchPipeline(
  options: RemapSearchPipelineOptions,
): Pipeline {
  const {
    query,
    contains = false,
    limit = DEFAULT_REMAP_SEARCH_LIMIT,
    maxNodes = 1000,
    maxRelationships = 5000,
    labels,
    filters,
  } = options;

  const trimmedQuery = query?.trim();
  if (!trimmedQuery) {
    throw new Error('buildRemapSearchPipeline: a non-empty query is required');
  }

  const cap = Math.max(1, Math.floor(limit));

  const searchArgs: Record<string, unknown> = {
    query: trimmedQuery,
    limit: cap,
  };
  if (contains) {
    searchArgs.contains = true;
  }
  // Both omitted entirely when empty. The backend distinguishes "no scope" from
  // an empty list, and an empty `filters` array reads as a filtered search that
  // happens to filter nothing.
  if (labels?.length) {
    searchArgs.labels = labels;
  }
  if (filters?.length) {
    searchArgs.filters = filters;
  }

  const steps: PipelineStep[] = [
    {
      op: 'search',
      args: searchArgs,
      as: REMAP_SEARCH_BINDINGS.HITS,
    },
    {
      op: 'rows_from_nodes',
      from: REMAP_SEARCH_BINDINGS.HITS,
      args: {
        // `id` plus relevance only. Every displayed property already arrives
        // on the graph nodes, so projecting them here would duplicate the
        // whole page — and `search_match_fragment` alone can be long.
        fields: ['id', ...SEARCH_RELEVANCE_COLUMNS],
        allow_mixed: true,
      },
      as: REMAP_SEARCH_BINDINGS.ROWS_RS,
    },
    {
      op: 'to_table',
      from: REMAP_SEARCH_BINDINGS.ROWS_RS,
      args: { limit: cap },
      as: REMAP_SEARCH_BINDINGS.ROWS,
    },
    {
      op: 'project_graph',
      from: REMAP_SEARCH_BINDINGS.HITS,
      args: {
        max_nodes: maxNodes,
        max_relationships: maxRelationships,
        induced: true,
      },
      as: REMAP_SEARCH_BINDINGS.GRAPH,
    },
  ];

  return {
    name: REMAP_PIPELINE_NAMES.SEARCH,
    steps,
    // Named bindings, not 'last': the graph and the relevance rows are two
    // sibling results of one search, and 'last' would drop whichever it is not.
    emit: [REMAP_SEARCH_BINDINGS.ROWS, REMAP_SEARCH_BINDINGS.GRAPH],
  };
}
